// Editorial district map for /electionday — two visual channels:
//
//  1. Partisan lean. Diverging PL-red ↔ PN-blue scale; intensity
//     scales with margin in percentage points. Default per-district
//     source is the real 2022 Electoral Commission result from
//     `properties.results_2022` (pl_pct / pn_pct / margin_pp). As
//     the newsroom enters 2026 first-count figures via /admin/
//     electionday, that district transitions to live 2026 data and
//     the label flips to "2026 — live"; until then it visibly
//     reads "2022 baseline". Honest labelling over forced symmetry
//     — different districts can sit in different states.
//
//  2. Count progress. A second visual channel: an SVG arc around
//     each district's centroid label, length proportional to
//     `count_progress_pct`. Only drawn for districts on 2026 live
//     data — a 2022-baseline district shows no ring (there's
//     nothing to count). 0% = no arc, 100% = full circle.
//
// Strict separation: the experimental sampling pipeline's tallies
// are NEVER read here. The map is editorial-only. The experimental
// sample readout lives in its own section below, behind its own
// "EXPERIMENTAL — INDICATIVE" framing.

import { useState, type CSSProperties } from 'react';
import { MAP_DATA, type BuiltPath } from '../../lib/buildMapPaths';
import { useElectionDayEditorial } from '../../lib/useElectionDayEditorial';
import type { DistrictEditorialResult } from '../../data/electionday-editorial-types';

/** District-level reading the map renders — either 2026 live data
 *  or the 2022 baseline fall-through. The discriminant `source`
 *  drives the label + the visual treatment. */
interface DistrictReading {
  source: '2026-live' | '2022-baseline';
  leader: 'PL' | 'PN' | 'tie' | null;
  /** Absolute margin in percentage points (e.g. 22.51 means 22.51pp). */
  marginPp: number;
  pl_pct: number | null;
  pn_pct: number | null;
  countProgressPct: number | null;
  /** Editorial status, only present when source === '2026-live'. */
  status: DistrictEditorialResult['status'] | null;
}

/** Districts 1–7 historically lean Labour; 8–12 Nationalist; 13
 *  (Gozo) is its own category. Surfaced in the hover tooltip per
 *  the established Maltese coverage convention. */
function partyLeanLabel(district: number): string {
  if (district === 13) return 'Gozo — distinct';
  if (district <= 7) return 'Labour-leaning (traditional)';
  return 'Nationalist-leaning (traditional)';
}

export interface EditorialDistrictMapProps {
  /** When set, the map renders an extra ring + a "flash" animation
   *  on the matching district. Driven by the paired count-progress
   *  map's "see lead →" affordance — the editor clicks there, the
   *  page-level state flips this prop, and this map briefly puts a
   *  visible halo on the district they wanted to look up. */
  highlightId?: number | null;
}

export default function EditorialDistrictMap({
  highlightId = null,
}: EditorialDistrictMapProps = {}) {
  const { data } = useElectionDayEditorial();
  const [hovered, setHovered] = useState<number | null>(null);

  const readings = computeReadings(data.districts);

  // Quick counters surfaced under the legend so the reader gets an
  // honest read of "how much of this map is 2026 live vs how much
  // is the 2022 fall-through".
  let live2026 = 0;
  let baseline2022 = 0;
  for (const p of MAP_DATA.paths) {
    const r = readings[`District ${p.id}`];
    if (r?.source === '2026-live') live2026++;
    else baseline2022++;
  }

  return (
    <section
      id="electionday-result-map"
      className="electionday-map"
      aria-label="Editorial district map"
    >
      <header className="electionday-map__head">
        <h3 className="electionday-map__title">
          Newsroom district map · 2026 live where available
        </h3>
        <Legend />
      </header>

      <p className="electionday-map__source-note">
        {live2026} district{live2026 === 1 ? '' : 's'} on 2026 live data ·{' '}
        {baseline2022} still on the 2022 baseline. Districts transition to
        live colour as the newsroom enters first-count figures; baseline
        districts are visibly dimmed so the difference is unmistakable.
      </p>

      <div className="electionday-map__shell">
        <svg
          viewBox={MAP_DATA.viewBox}
          role="img"
          aria-label="Malta electoral districts coloured by 2026 newsroom result with 2022 baseline fall-through"
          className="electionday-map__svg"
        >
          <g>
            {MAP_DATA.paths.map((p) =>
              renderPath(p, readings[`District ${p.id}`], setHovered),
            )}
          </g>

          {/* Cross-map "see lead" highlight — sits BETWEEN the fills
              and the labels so the halo wraps the district outline
              but doesn't obscure the centroid number. A CSS keyframe
              gives it a short pulse so the editor can find the
              jumped-to district without losing the page. */}
          {highlightId !== null && (
            <g pointerEvents="none">
              {MAP_DATA.paths
                .filter((p) => p.id === highlightId)
                .map((p) => (
                  <path
                    key={`hl-${p.id}-${highlightId}`}
                    d={p.path}
                    fill="none"
                    stroke="#FFC542"
                    strokeWidth={5}
                    strokeLinejoin="round"
                    strokeLinecap="round"
                    vectorEffect="non-scaling-stroke"
                    className="electionday-map__highlight"
                  />
                ))}
            </g>
          )}

          {/* Labels + count-progress rings last so they sit on top
              of every fill. */}
          <g pointerEvents="none">
            {MAP_DATA.paths.map((p) => {
              const r = readings[`District ${p.id}`];
              return (
                <DistrictLabel
                  key={`l${p.id}`}
                  path={p}
                  reading={r}
                />
              );
            })}
          </g>
        </svg>

        {hovered !== null && (
          <HoverTip
            districtId={hovered}
            districtLabel={`District ${hovered}`}
            reading={readings[`District ${hovered}`]}
            editorial={data.districts[`District ${hovered}`]}
            baseline={
              MAP_DATA.paths.find((p) => p.id === hovered)?.properties
                .results_2022
            }
          />
        )}
      </div>
    </section>
  );
}

function renderPath(
  p: BuiltPath,
  reading: DistrictReading | undefined,
  onHover: (n: number | null) => void,
) {
  const fill = fillFor(reading);
  const isBaseline = reading?.source === '2022-baseline';
  return (
    <path
      key={p.id}
      d={p.path}
      fill={fill}
      stroke="#1f1f1f"
      strokeWidth={1.4}
      strokeLinejoin="round"
      strokeLinecap="round"
      strokeDasharray={isBaseline ? '4,3' : undefined}
      vectorEffect="non-scaling-stroke"
      className={`electionday-map__district${isBaseline ? ' is-baseline' : ' is-live'}`}
      onMouseEnter={() => onHover(p.id)}
      onMouseLeave={() => onHover(null)}
    />
  );
}

function fillFor(reading: DistrictReading | undefined): string {
  if (!reading || reading.leader === null) return '#e5e5e5';
  if (reading.leader === 'tie') return '#bdbdbd';
  // Saturation ramp:
  //   - 2026 live: 0pp → 0.35 alpha, 30pp+ → 0.95 alpha (vivid)
  //   - 2022 baseline: same curve, capped lower (0.6) AND tinted
  //     toward grey via lower alpha + dashed stroke so the eye
  //     reads it as "historical" rather than "current".
  const intensity = Math.min(1, Math.max(0.35, reading.marginPp / 30));
  const cap = reading.source === '2026-live' ? 0.95 : 0.6;
  const a = Math.min(cap, intensity);
  const base = reading.leader === 'PL' ? '255, 19, 73' : '25, 54, 160';
  return `rgba(${base}, ${a.toFixed(2)})`;
}

// ── District label + count-progress ring ──────────────────────────

interface DistrictLabelProps {
  path: BuiltPath;
  reading: DistrictReading | undefined;
}

function DistrictLabel({ path, reading }: DistrictLabelProps) {
  const labelR = 16;
  const ringR = labelR + 6;
  const progress =
    reading?.source === '2026-live' && reading.countProgressPct !== null
      ? Math.max(0, Math.min(100, reading.countProgressPct))
      : null;
  const isBaseline = reading?.source === '2022-baseline';

  return (
    <g>
      {/* Progress ring — only on 2026-live districts that have a
          count_progress_pct. Drawn as an arc starting at the 12-
          o'clock position, clockwise, length = progress / 100. */}
      {progress !== null && (
        <ProgressRing
          cx={path.labelX}
          cy={path.labelY}
          r={ringR}
          pct={progress}
        />
      )}

      <circle
        cx={path.labelX}
        cy={path.labelY}
        r={labelR}
        fill="rgba(0,0,0,0.9)"
      />
      <text
        x={path.labelX}
        y={path.labelY}
        textAnchor="middle"
        dominantBaseline="central"
        fontFamily="'Poppins', sans-serif"
        fontSize="14"
        fontWeight="800"
        fill="#fff"
      >
        {path.id}
      </text>

      {/* Source tag — small "'22" badge in the corner of baseline
          districts so the map can be read at a glance even without
          hovering. 2026-live districts get no tag — full colour +
          progress ring is the visual cue. */}
      {isBaseline && (
        <g
          transform={`translate(${path.labelX + labelR + 6}, ${path.labelY - labelR + 2})`}
        >
          <rect
            x={-1}
            y={-7}
            width={18}
            height={12}
            fill="rgba(0,0,0,0.8)"
            rx={2}
          />
          <text
            x={8}
            y={0}
            textAnchor="middle"
            dominantBaseline="central"
            fontFamily="'Poppins', sans-serif"
            fontSize="8"
            fontWeight="800"
            fill="#fff"
            letterSpacing="0.5"
          >
            '22
          </text>
        </g>
      )}
    </g>
  );
}

interface ProgressRingProps {
  cx: number;
  cy: number;
  r: number;
  pct: number;
}

/** SVG arc from 12 o'clock, clockwise, covering `pct / 100` of the
 *  circle. 0% → invisible; 100% → full circle. Stroke is a
 *  partisan-neutral mid-grey so it doesn't conflict with the fill
 *  colour of the district behind it. */
function ProgressRing({ cx, cy, r, pct }: ProgressRingProps) {
  const clamped = Math.max(0, Math.min(100, pct));
  if (clamped <= 0) {
    return (
      <circle
        cx={cx}
        cy={cy}
        r={r}
        fill="none"
        stroke="rgba(255,255,255,0.35)"
        strokeWidth={2.5}
        strokeDasharray="2,3"
      />
    );
  }
  if (clamped >= 100) {
    return (
      <circle
        cx={cx}
        cy={cy}
        r={r}
        fill="none"
        stroke="#2c8a4a"
        strokeWidth={3.5}
      />
    );
  }
  const angle = (clamped / 100) * 2 * Math.PI;
  // 12 o'clock = (cx, cy - r). Clockwise sweep.
  const x1 = cx;
  const y1 = cy - r;
  const x2 = cx + r * Math.sin(angle);
  const y2 = cy - r * Math.cos(angle);
  const largeArc = clamped > 50 ? 1 : 0;
  const d = `M ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2}`;
  return (
    <>
      <circle
        cx={cx}
        cy={cy}
        r={r}
        fill="none"
        stroke="rgba(255,255,255,0.35)"
        strokeWidth={2.5}
        strokeDasharray="2,3"
      />
      <path d={d} fill="none" stroke="#2c8a4a" strokeWidth={3.5} />
    </>
  );
}

// ── Legend + hover tooltip ────────────────────────────────────────

function Legend() {
  const swatch = (color: string): CSSProperties => ({
    background: color,
    width: 14,
    height: 14,
    borderRadius: 3,
    border: '1px solid rgba(0,0,0,0.15)',
    display: 'inline-block',
    marginRight: 6,
    verticalAlign: 'middle',
  });
  return (
    <div className="electionday-map__legend">
      <span>
        <span style={swatch('rgba(255, 19, 73, 0.95)')} />
        PL · live
      </span>
      <span>
        <span style={swatch('rgba(25, 54, 160, 0.95)')} />
        PN · live
      </span>
      <span>
        <span style={swatch('rgba(255, 19, 73, 0.45)')} />
        2022 baseline
      </span>
      <span className="electionday-map__legend-ring">
        <svg width={18} height={18} viewBox="0 0 18 18" aria-hidden="true">
          <circle
            cx={9}
            cy={9}
            r={7}
            fill="none"
            stroke="rgba(0,0,0,0.25)"
            strokeWidth={2}
            strokeDasharray="2,3"
          />
          <path
            d="M 9 2 A 7 7 0 0 1 16 9"
            fill="none"
            stroke="#2c8a4a"
            strokeWidth={2.5}
          />
        </svg>
        count progress
      </span>
    </div>
  );
}

interface HoverTipProps {
  districtId: number;
  districtLabel: string;
  reading: DistrictReading | undefined;
  editorial: DistrictEditorialResult | undefined;
  /** Real 2022 result for the same district. When the hovered
   *  district is reading 2026-live we surface a small "vs 2022"
   *  comparison row so the swing is visible at a glance. */
  baseline:
    | {
        pl_pct: number;
        pn_pct: number;
        margin_pp: number;
        winner: string;
      }
    | undefined;
}

function HoverTip({
  districtId,
  districtLabel,
  reading,
  editorial,
  baseline,
}: HoverTipProps) {
  const traditional = partyLeanLabel(districtId);
  if (!reading) {
    return (
      <div className="electionday-map__tip">
        <strong>{districtLabel}</strong>
        <p>{traditional}</p>
        <p>No data — geometry only.</p>
      </div>
    );
  }
  const leaderLabel =
    reading.leader === 'PL'
      ? 'PL ahead'
      : reading.leader === 'PN'
        ? 'PN ahead'
        : reading.leader === 'tie'
          ? 'Tied'
          : '—';
  return (
    <div className="electionday-map__tip">
      <strong>{districtLabel}</strong>
      <p>
        <span
          className={`electionday-map__tip-source electionday-map__tip-source--${
            reading.source === '2026-live' ? 'live' : 'baseline'
          }`}
        >
          {reading.source === '2026-live' ? '2026 — live' : '2022 baseline'}
        </span>
      </p>
      <p>
        {leaderLabel}
        {reading.marginPp > 0 && ` · +${reading.marginPp.toFixed(1)}pp`}
      </p>
      <p>
        PL {reading.pl_pct !== null ? `${reading.pl_pct.toFixed(1)}%` : '—'} ·
        PN {reading.pn_pct !== null ? `${reading.pn_pct.toFixed(1)}%` : '—'}
      </p>
      {reading.source === '2026-live' && (
        <p>
          Count progress:{' '}
          {reading.countProgressPct !== null
            ? `${Math.round(reading.countProgressPct)}%`
            : '—'}
        </p>
      )}
      {reading.source === '2026-live' && baseline && reading.pl_pct !== null && (
        <div className="electionday-map__tip-baseline">
          <span className="electionday-map__tip-baseline-label">
            vs 2022
          </span>
          <span>
            PL {baseline.pl_pct.toFixed(1)}% · PN {baseline.pn_pct.toFixed(1)}% ·{' '}
            {baseline.winner} +{Math.abs(baseline.margin_pp).toFixed(1)}pp
          </span>
          <span
            className={`electionday-map__tip-swing electionday-map__tip-swing--${swingTone(reading.pl_pct - baseline.pl_pct)}`}
          >
            PL swing {formatSwing(reading.pl_pct - baseline.pl_pct)}
          </span>
        </div>
      )}
      <p className="electionday-map__tip-tradition">{traditional}</p>
      {editorial?.note && (
        <p className="electionday-map__tip-note">{editorial.note}</p>
      )}
    </div>
  );
}

// ── Reading computation ────────────────────────────────────────────

function computeReadings(
  editorial: Record<string, DistrictEditorialResult>,
): Record<string, DistrictReading> {
  const out: Record<string, DistrictReading> = {};
  for (const path of MAP_DATA.paths) {
    const key = `District ${path.id}`;
    const ed = editorial[key];
    out[key] = readingFor(path, ed);
  }
  return out;
}

function readingFor(
  path: BuiltPath,
  editorial: DistrictEditorialResult | undefined,
): DistrictReading {
  const live = liveFrom(editorial);
  if (live) return live;
  return baselineFrom(path);
}

/** Derive a 2026-live reading from the editorial entry — but only
 *  if it carries enough data to actually pick a leader. A district
 *  marked "counting" with no votes entered yet still belongs in the
 *  2022 baseline (otherwise the map would render an empty 2026
 *  district as a grey blob, losing the partisan signal entirely). */
function liveFrom(
  editorial: DistrictEditorialResult | undefined,
): DistrictReading | null {
  if (!editorial) return null;
  if (
    editorial.pl_votes === null ||
    editorial.pn_votes === null ||
    editorial.total_valid === null ||
    editorial.total_valid <= 0
  ) {
    // Editor has touched the row (maybe entered count_progress
    // already) but not yet entered vote splits. Don't pretend to
    // know who's ahead — fall back to 2022 for the lean colour,
    // but carry forward the count_progress so the ring still draws.
    if (
      editorial.count_progress_pct !== null &&
      editorial.count_progress_pct > 0
    ) {
      return null;
    }
    return null;
  }
  const pl_pct = (editorial.pl_votes / editorial.total_valid) * 100;
  const pn_pct = (editorial.pn_votes / editorial.total_valid) * 100;
  const marginPp = Math.abs(pl_pct - pn_pct);
  const leader: DistrictReading['leader'] =
    editorial.pl_votes > editorial.pn_votes
      ? 'PL'
      : editorial.pn_votes > editorial.pl_votes
        ? 'PN'
        : 'tie';
  return {
    source: '2026-live',
    leader,
    marginPp,
    pl_pct,
    pn_pct,
    countProgressPct: editorial.count_progress_pct,
    status: editorial.status,
  };
}

function baselineFrom(path: BuiltPath): DistrictReading {
  const r = path.properties.results_2022;
  const leader: DistrictReading['leader'] =
    r.winner === 'PL' ? 'PL' : r.winner === 'PN' ? 'PN' : 'tie';
  return {
    source: '2022-baseline',
    leader,
    marginPp: Math.abs(r.margin_pp ?? 0),
    pl_pct: r.pl_pct,
    pn_pct: r.pn_pct,
    countProgressPct: null,
    status: null,
  };
}

function formatSwing(deltaPct: number): string {
  if (deltaPct > 0) return `+${deltaPct.toFixed(1)}pp`;
  if (deltaPct < 0) return `${deltaPct.toFixed(1)}pp`;
  return 'flat';
}

function swingTone(deltaPct: number): 'up' | 'down' | 'flat' {
  if (deltaPct > 0.5) return 'up';
  if (deltaPct < -0.5) return 'down';
  return 'flat';
}
